import { useCallback, useRef, useState } from 'react'

import {
  type UseQueryResult,
  useQueries,
  useQueryClient,
} from '@tanstack/react-query'
import { debounce } from 'es-toolkit/compat'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import { sbirAgenciesQueryKey } from '@/hooks/sbir/types'
import { combineQueryResults as baseCombineQueryResults } from '@/hooks/search/lib'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { SbirAgency } from '@/types/sbir/Filter'

interface UseSbirAgencySearchArgs {
  initialAgencyIds?: string[]
}

export const fetchSbirAgencies = async (
  query: string,
  accessToken: string | undefined | null,
  pagination: PaginateRequest,
): Promise<PaginatedResponse<SbirAgency>> => {
  if (accessToken === null || accessToken === undefined || query.length < 1) {
    return { count: 0, has_more: false, data: [] }
  }
  const url = `${API_URL}/api/v1/sbirs/sbir-agency/search?query=${query}&limit=${pagination.limit}`
  const res: Response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }
  return (await res.json()) as PaginatedResponse<SbirAgency>
}

export const useSbirAgencySearch = ({
  initialAgencyIds,
}: UseSbirAgencySearchArgs) => {
  const { accessToken } = useAuthInfoWithThrow()

  const queryClient = useQueryClient()

  const cacheRef = useRef<Map<string, SbirAgency>>(new Map())

  const [searchTerm, setSearchTerm] = useState<string>('')

  const debouncedFetch = debounce(fetchSbirAgencies, 150, {
    leading: true,
    trailing: false,
  })

  const combineQueryResults = useCallback(
    (queries: UseQueryResult<PaginatedResponse<SbirAgency> | undefined>[]) => {
      const [itemsQuery, searchQuery] = queries
      return baseCombineQueryResults({
        searchTerm,
        cacheRef,
        queryClient,
        itemsQuery,
        searchQuery,
        itemIdFn: (item: SbirAgency) => item.name,
        searchQueryKeyFn: (query: string) =>
          sbirAgenciesQueryKey.search({ query }),
        itemsQueryKeyFn: (items: string[]) =>
          sbirAgenciesQueryKey.agencies({ agencyIds: items }),
        initialItemIds: initialAgencyIds,
      })
    },
    [initialAgencyIds, searchTerm, queryClient],
  )

  const query = useQueries({
    queries: [
      {
        // TODO: add logic for this when we have an agency endpoint
        queryKey: sbirAgenciesQueryKey.agencies({
          agencyIds: initialAgencyIds ?? [],
        }),
        queryFn: () => {
          return {
            count: initialAgencyIds?.length ?? 0,
            has_more: false,
            data: initialAgencyIds?.map((name) => ({ name })) ?? [],
          }
        },
        staleTime: Infinity,
      },
      {
        queryKey: sbirAgenciesQueryKey.search({ query: searchTerm }),
        queryFn: () =>
          debouncedFetch(searchTerm, accessToken, {
            limit: 50,
          }),
        staleTime: Infinity,
      },
    ],
    combine: combineQueryResults,
  })

  return {
    ...query,
    cache: cacheRef.current,
    query: searchTerm,
    setQuery: setSearchTerm,
  }
}
